import { BaseType } from "antd/es/typography/Base";
import Parse from "parse";
import {
  Agency,
  Calendar,
  Route,
  Stop,
  Stop_Times,
  Trip,
} from "../parsegenerated";
import { AgencyModel } from "./models/agency";
import { RouteModel } from "./models/route";
import { StopModel } from "./models/stop";
import { StopTimeModel } from "./models/stopTime";
import { TripModel } from "./models/trip";

export type JSONData = {
  agency: AgencyModel;
  routes: RouteModel[];
  stops: StopModel[];
  stopTimes: StopTimeModel[];
  trips: TripModel[];
};

export type MessagePush = {
  text: string;
  status: BaseType;
  translationData?: { [key: string]: string };
};

export class KommuterConverter {
  private data: JSONData;
  private onStep: (message: MessagePush) => void;
  private onSuccess: (message: MessagePush) => void;
  private onError: (message: MessagePush) => void;
  private onWarning: (message: MessagePush) => void;

  private hasFatalError: boolean = false;
  private agency: Agency;
  private routes: Route[] = [];
  private stops: Stop[] = [];
  private trips: Trip[] = [];
  private stopTimes: Stop_Times[] = [];

  constructor(
    data: JSONData,
    onStep: (message: MessagePush) => void,
    onSuccess: (message: MessagePush) => void,
    onError: (message: MessagePush) => void,
    onWarning: (message: MessagePush) => void
  ) {
    this.data = data;
    this.onStep = (message: MessagePush) => onStep(message);
    this.onSuccess = (message: MessagePush) => onSuccess(message);
    this.onError = (message: MessagePush) => onError(message);
    this.onWarning = (message: MessagePush) => onWarning(message);
  }

  public async convertAgency() {
    this.pushStep("Converting agency...");
    const agencyExists = await new Parse.Query(Agency)
      .equalTo("agency_name", this.data.agency.agency_name)
      .first();
    if (agencyExists) {
      this.pushWarning("Agency already exists. Skipping conversion.");
      this.agency = agencyExists;
      return;
    }
    try {
      this.agency = await new Agency({
        agency_id: this.data.agency.agency_id,
        agency_name: this.data.agency.agency_name,
        agency_url: this.data.agency.agency_url,
        agency_lang: this.data.agency.agency_lang,
        agency_email: this.data.agency.agency_email,
        tenant: Parse.User.current().get("tenant"),
        agency_timezone: "Europe/Berlin",
      }).save();
      this.pushSuccess("Agency converted");
    } catch (error) {
      console.error(error);
      this.pushError("Failed to convert agency");
      this.hasFatalError = true;
    }
  }

  public async convertRoutes(route_type: number) {
    this.pushStep("Converting routes...");
    const existingRoutes = await new Parse.Query(Route)
      .equalTo("agency_id", this.agency)
      .limit(10000000)
      .find();

    for (const route of this.data.routes) {
      const routeExists = existingRoutes.find(
        (r) => r.get("route_id") === route.route_id
      );
      if (routeExists) {
        this.routes.push(routeExists);
        this.pushWarning(
          `Route '${route.route_long_name}' already exists. Skipping conversion.`
        );
        continue;
      }
      try {
        const savedRoute = await new Route({
          route_id: route.route_id,
          agency_id: this.agency,
          route_short_name: route.route_short_name,
          route_long_name: route.route_long_name,
          route_desc: route.route_desc,
          route_color: route.route_color,
          route_text_color: route.route_text_color,
          tenant: Parse.User.current().get("tenant"),
          route_type: route_type,
          key: route.route_long_name ?? "Unbekannte Linie",
        }).save();

        this.routes.push(savedRoute);
      } catch (error) {
        console.error(error);
        this.pushError("Failed to convert route " + route.route_long_name);
      }
    }
    this.pushSuccess("Routes converted");
  }

  public async convertStops() {
    this.pushStep("Converting stops...");
    for (const stop of this.data.stops) {
      try {
        const stopExists = await new Parse.Query(Stop)
          .equalTo("stop_id", stop.stop_id)
          .first();

        if (stopExists) {
          this.stops.push(stopExists);

          this.pushWarning(
            `Stop '${stop.stop_name}' already exists. Skipping conversion.`
          );
          continue;
        }

        const savedStop = await new Stop({
          stop_id: stop.stop_id,
          stop_name: stop.stop_name,
          stop_lat: stop.stop_lat.toString(),
          stop_lon: stop.stop_lon.toString(),
          tenant: Parse.User.current().get("tenant"),
        }).save();

        this.stops.push(savedStop);
      } catch (error) {
        console.error(error);
        this.pushError("Failed to convert stop " + stop.stop_name);
      }
    }
    this.pushSuccess("Stops converted");
  }

  public async convertTrips(start: Date, end: Date) {
    this.pushStep("Converting trips...");
    for (const trip of this.data.trips) {
      try {
        const tripExists = await new Parse.Query(Trip)
          .equalTo("key", trip.trip_id)
          .first();
        if (tripExists) {
          this.trips.push(tripExists);
          this.pushWarning(
            `Trip '${trip.trip_headsign}' already exists. Skipping conversion.`
          );
          continue;
        }
        const savedCalendar = await new Calendar({
          monday: trip.trip_weekdays.includes(1),
          tuesday: trip.trip_weekdays.includes(2),
          wednesday: trip.trip_weekdays.includes(3),
          thursday: trip.trip_weekdays.includes(4),
          friday: trip.trip_weekdays.includes(5),
          saturday: trip.trip_weekdays.includes(6),
          sunday: trip.trip_weekdays.includes(7),
          tenant: Parse.User.current().get("tenant"),
          start_date: start,
          end_date: end,
        }).save();

        const savedTrip = await new Trip({
          route_id: this.routes.find(
            (r) => r.get("route_id") === trip.route_id
          ),
          trip_headsign: trip.trip_headsign,
          trip_short_name: trip.trip_short_name,
          key: trip.trip_id,
          tenant: Parse.User.current().get("tenant"),
          service_id: savedCalendar,
        }).save();

        this.trips.push(savedTrip);
      } catch (error) {
        console.error(error);
        this.pushError("Failed to convert trip " + trip.trip_id);
      }
    }
    this.pushSuccess("Trips converted");
  }

  public async convertStopTimes() {
    this.pushStep("Converting stop times...");
    for (const stopTime of this.data.stopTimes) {
      try {
        const arrival_time = new Date(Number.parseInt(stopTime.arrival_time));
        const departure_time = new Date(
          Number.parseInt(stopTime.departure_time)
        );
        const stopTimeExists = await new Parse.Query(Stop_Times)
          .equalTo(
            "trip_id",
            this.trips.find((t) => t.get("key") === stopTime.trip_id)
          )
          .equalTo(
            "stop_id",
            this.stops.find((s) => s.get("stop_id") === stopTime.stop_id)
          )
          .equalTo("departure_time", departure_time)
          .equalTo("arrival_time", arrival_time)
          .first();
        if (stopTimeExists) {
          this.stopTimes.push(stopTimeExists);
          this.pushWarning(
            `Stop time for trip '${stopTime.trip_id}' at stop '${stopTime.stop_id}' already exists. Skipping conversion.`
          );
          continue;
        }
        const savedStopTime = await new Stop_Times({
          trip_id: this.trips.find((t) => t.get("key") === stopTime.trip_id),
          stop_id: this.stops.find(
            (s) => s.get("stop_id") === stopTime.stop_id
          ),
          stop_sequence: stopTime.stop_sequence,
          arrival_time: arrival_time,
          departure_time: departure_time,
          tenant: Parse.User.current().get("tenant"),
        }).save();

        this.stopTimes.push(savedStopTime);
      } catch (error) {
        console.error(error);
        this.pushError(
          "Failed to convert stop time for trip " + stopTime.trip_id
        );
      }
    }
    this.pushSuccess("Stop times converted");
  }

  private pushStep(text: string) {
    this.onStep({ text, status: "secondary" });
  }

  private pushError(text: string) {
    this.onError({ text, status: "danger" });
  }

  private pushSuccess(text: string) {
    this.onSuccess({ text, status: "success" });
  }

  private pushWarning(text: string) {
    this.onWarning({ text, status: "warning" });
  }
}
